import { ENGINE_SOUNDS } from '../../src/game/data.js';
import {
  inspectEngineSample,
  previewEngine,
  previewEngineSample,
  startEngineSampleLoop,
} from './proceduralSound.js';

const $=(root,selector)=>root.querySelector(selector);
const $$=(root,selector)=>[...root.querySelectorAll(selector)];

function cleanId(value){
  return String(value||'sonido-motor-personalizado')
    .normalize('NFD').replace(/[\u0300-\u036f]/g,'')
    .replace(/[^a-zA-Z0-9_-]+/g,'-').replace(/^-+|-+$/g,'').toLowerCase()
    ||'sonido-motor-personalizado';
}

function cloneDesign(source={}){
  return {
    wave:['sine','triangle','sawtooth','square'].includes(source.wave)?source.wave:'sawtooth',
    baseTone:Number(source.baseTone??58),
    speedPitch:Number(source.speedPitch??7),
    boostPitch:Number(source.boostPitch??45),
    filterType:['lowpass','bandpass','highpass'].includes(source.filterType)?source.filterType:'lowpass',
    filter:Number(source.filter??620),
    resonance:Number(source.resonance??1),
    volume:Number(source.volume??.035),
    pulse:Number(source.pulse??6),
    noiseGain:Number(source.noiseGain??.005),
    sampleIdleRate:Number(source.sampleIdleRate??1),
    sampleSpeedRate:Number(source.sampleSpeedRate??.04),
    sampleBoostRate:Number(source.sampleBoostRate??.35),
    sampleVolume:Number(source.sampleVolume??.28),
    sampleFilter:Number(source.sampleFilter??12000),
    sampleLoopStart:Number(source.sampleLoopStart??0),
    sampleLoopEndTrim:Number(source.sampleLoopEndTrim??0),
    sampleLoopCrossfade:Number(source.sampleLoopCrossfade??.04),
  };
}

export class EngineSoundEditor{
  constructor(screen,callbacks={}){
    this.screen=screen;
    this.callbacks=callbacks;
    this.status=$(screen,'#engine-sound-editor-message');
    this.template=$(screen,'#engine-sound-editor-template');
    this.fileInput=$(screen,'#engine-sound-editor-file');
    this.fileName=$(screen,'#engine-sound-editor-file-name');
    this.fileInfo=$(screen,'#engine-sound-editor-file-info');
    this.saveButton=$(screen,'#engine-sound-editor-save');
    this.testButton=$(screen,'#engine-sound-editor-test');
    this.loopButton=$(screen,'#engine-sound-editor-loop');
    this.mode='procedural';
    this.assetFile=null;
    this.assetStats=null;
    this.loopPreview=null;
    this.loopStarting=false;
    this.loopRestartTimer=null;
    this.loopStartToken=0;
    this.design=cloneDesign(ENGINE_SOUNDS[0]);
    $(screen,'#engine-sound-editor-close').addEventListener('click',()=>this.close());
    this.saveButton.addEventListener('click',()=>this.save());
    this.testButton.addEventListener('click',()=>this.test());
    this.loopButton.addEventListener('click',()=>this.toggleLoopPreview());
    $(screen,'#engine-sound-editor-reset').addEventListener('click',()=>this.reset());
    $$ (screen,'[data-engine-sound-mode]').forEach((button)=>{
      button.addEventListener('click',()=>this.setMode(button.dataset.engineSoundMode));
    });
    this.fileInput.addEventListener('change',()=>this.loadAudioFile(this.fileInput.files[0]));
    this.template.addEventListener('change',()=>this.reset());
    $$ (screen,'[data-engine-sound-design]').forEach((input)=>{
      input.addEventListener('input',()=>{
        const key=input.dataset.engineSoundDesign;
        this.design[key]=input.tagName==='SELECT'?input.value:Number(input.value);
        input.syncNumberControl?.();
        if(this.loopPreview){
          if(key.startsWith('sampleLoop'))this.scheduleLoopRestart();
          else this.loopPreview.update(this.design);
        }
      });
    });
    this.sync();
  }

  sync(){
    $$ (this.screen,'[data-engine-sound-design]').forEach((input)=>{
      input.value=this.design[input.dataset.engineSoundDesign];
      input.syncNumberControl?.();
    });
    $$ (this.screen,'[data-engine-sound-mode]').forEach((button)=>{
      button.classList.toggle('selected',button.dataset.engineSoundMode===this.mode);
    });
    $$ (this.screen,'[data-engine-sound-panel]').forEach((panel)=>{
      panel.classList.toggle('hidden',panel.dataset.engineSoundPanel!==this.mode);
    });
    this.saveButton.disabled=this.mode==='mp3'&&!this.assetFile;
    this.syncLoopButton();
  }

  syncLoopButton(){
    const playing=Boolean(this.loopPreview);
    this.loopButton.classList.toggle('playing',playing);
    this.loopButton.textContent=playing?'■ DETENER LOOP':'↻ ESCUCHAR LOOP CONTINUO';
    this.loopButton.disabled=(
      this.mode!=='mp3'
      ||!this.assetFile
      ||this.loopStarting
    );
  }

  setStatus(message,state='ready'){
    this.status.textContent=message;
    this.status.dataset.state=state;
  }

  setMode(mode){
    if(mode!=='procedural'&&mode!=='mp3')return;
    if(mode!=='mp3')this.stopLoopPreview();
    this.mode=mode;
    this.sync();
    if(mode==='mp3'){
      this.setStatus(
        this.assetFile
          ?'Loop MP3 listo. Probalo para escuchar cómo sube de revoluciones.'
          :'Elegí un MP3 de motor regulando para crear el loop.',
        this.assetFile?'success':'ready',
      );
    }else{
      this.setStatus('Sonido procedural listo para probar.');
    }
  }

  async loadAudioFile(file,{applySuggestedTrim=true}={}){
    if(!file)return;
    this.stopLoopPreview();
    this.loopButton.disabled=true;
    this.setStatus('Analizando el loop MP3…');
    this.saveButton.disabled=true;
    try{
      const stats=await inspectEngineSample(file);
      this.mode='mp3';
      this.assetFile=file;
      this.assetStats=stats;
      if(applySuggestedTrim){
        this.design.sampleLoopStart=stats.suggestedStartTrim;
        this.design.sampleLoopEndTrim=stats.suggestedEndTrim;
      }
      this.fileName.textContent=file.name;
      this.fileInfo.textContent=[
        `${stats.duration.toFixed(2)} s`,
        `${stats.sampleRate} Hz`,
        stats.channels===1?'mono':`${stats.channels} canales`,
        `recorte ${Math.round(this.design.sampleLoopStart*1000)}/${Math.round(this.design.sampleLoopEndTrim*1000)} ms`,
        stats.warnings.length?'revisar loop':'listo para repetir',
      ].join(' · ');
      this.sync();
      this.setStatus(
        stats.warnings[0]
          ||'MP3 validado. Se aplicará recorte de padding y fundido en la unión del loop.',
        stats.warnings.length?'warning':'success',
      );
    }catch(error){
      console.error(error);
      this.assetFile=null;
      this.assetStats=null;
      this.fileInput.value='';
      this.fileName.textContent='Elegir archivo MP3';
      this.fileInfo.textContent='No se pudo validar el archivo';
      this.sync();
      this.setStatus(error.message||'No se pudo abrir el MP3.','error');
    }
  }

  async test(){
    if(this.mode==='mp3'&&!this.assetFile){
      this.setStatus('Cargá un MP3 antes de probar el loop.','warning');
      return;
    }
    this.stopLoopPreview();
    this.testButton.disabled=true;
    try{
      if(this.mode==='mp3')await previewEngineSample(this.assetFile,this.design);
      else previewEngine(this.design);
      this.setStatus(
        this.mode==='mp3'
          ?'Prueba MP3: ralentí, aceleración, nitro y regreso a ralentí.'
          :'Prueba procedural: ralentí, aceleración, nitro y regreso a ralentí.',
        'success',
      );
    }catch(error){
      console.error(error);
      this.setStatus(error.message||'No se pudo reproducir el sonido.','error');
    }finally{
      this.testButton.disabled=false;
    }
  }

  reset(){
    this.design=cloneDesign(ENGINE_SOUNDS.find((item)=>item.id===this.template.value)||ENGINE_SOUNDS[0]);
    if(this.mode==='mp3'&&this.assetStats){
      this.design.sampleLoopStart=Number(this.assetStats.suggestedStartTrim)||0;
      this.design.sampleLoopEndTrim=Number(this.assetStats.suggestedEndTrim)||0;
    }
    this.sync();
    if(this.loopPreview)this.scheduleLoopRestart();
  }

  async toggleLoopPreview(){
    if(this.loopPreview||this.loopStarting){
      this.stopLoopPreview(true);
      return;
    }
    await this.startLoopPreview();
  }

  async startLoopPreview({announce=true}={}){
    if(this.mode!=='mp3'||!this.assetFile){
      this.setStatus('Cargá un MP3 antes de escuchar el loop.','warning');
      return;
    }
    if(this.loopRestartTimer){
      window.clearTimeout(this.loopRestartTimer);
      this.loopRestartTimer=null;
    }
    const token=++this.loopStartToken;
    this.loopStarting=true;
    this.syncLoopButton();
    try{
      const controller=await startEngineSampleLoop(this.assetFile,this.design);
      if(token!==this.loopStartToken){
        controller.stop();
        return;
      }
      this.loopPreview=controller;
      if(announce){
        this.setStatus(
          'Loop continuo activo. Ajustá recortes y fundido hasta que la unión desaparezca.',
          'success',
        );
      }
    }catch(error){
      if(token!==this.loopStartToken)return;
      console.error(error);
      this.setStatus(error.message||'No se pudo iniciar el loop.','error');
    }finally{
      if(token===this.loopStartToken){
        this.loopStarting=false;
        this.syncLoopButton();
      }
    }
  }

  scheduleLoopRestart(){
    if(!this.loopPreview)return;
    if(this.loopRestartTimer)window.clearTimeout(this.loopRestartTimer);
    this.loopRestartTimer=window.setTimeout(()=>{
      this.loopRestartTimer=null;
      this.startLoopPreview({announce:false});
    },120);
  }

  stopLoopPreview(announce=false){
    if(this.loopRestartTimer){
      window.clearTimeout(this.loopRestartTimer);
      this.loopRestartTimer=null;
    }
    this.loopStartToken+=1;
    this.loopStarting=false;
    this.loopPreview?.stop();
    this.loopPreview=null;
    this.syncLoopButton();
    if(announce)this.setStatus('Escucha continua detenida.');
  }

  clearWorkspace(){
    this.stopLoopPreview();
    this.template.value=ENGINE_SOUNDS[0].id;
    this.mode='procedural';
    this.assetFile=null;
    this.assetStats=null;
    this.fileInput.value='';
    this.fileName.textContent='Elegir archivo MP3';
    this.fileInfo.textContent='Ralentí estable · loop recomendado de 2 a 10 segundos';
    this.design=cloneDesign(ENGINE_SOUNDS[0]);
    this.sync();
    this.setStatus('Sonido procedural listo para probar.');
  }

  async loadRecord(item){
    this.stopLoopPreview();
    const savedDesign=item.profile?.design||ENGINE_SOUNDS[0];
    const hasSavedLoopSettings=(
      savedDesign.sampleLoopStart!==undefined
      ||savedDesign.sampleLoopEndTrim!==undefined
      ||savedDesign.sampleLoopCrossfade!==undefined
    );
    this.mode=item.profile?.mode==='mp3'?'mp3':'procedural';
    this.assetFile=item.assetFile||null;
    this.assetStats=item.profile?.asset||null;
    this.fileInput.value='';
    this.fileName.textContent=this.assetFile?.name||this.assetStats?.fileName||'Elegir archivo MP3';
    this.fileInfo.textContent=this.assetStats?.duration
      ?`${Number(this.assetStats.duration).toFixed(2)} s · loop de motor`
      :'Ralentí estable · loop recomendado de 2 a 10 segundos';
    this.design=cloneDesign(savedDesign);
    this.sync();
    if(this.mode==='mp3'&&this.assetFile){
      await this.loadAudioFile(this.assetFile,{applySuggestedTrim:!hasSavedLoopSettings});
    }
    else if(this.mode==='mp3')this.setStatus('No se encontró el MP3 guardado para este sonido.','warning');
    else this.setStatus('Sonido procedural cargado.');
  }

  open(){this.callbacks.onOpen?.();}
  close(){
    this.stopLoopPreview();
    this.callbacks.onClose?.();
  }

  async save(){
    const id=cleanId($(this.screen,'#engine-sound-editor-id').value);
    $(this.screen,'#engine-sound-editor-id').value=id;
    if(this.mode==='mp3'&&!this.assetFile){
      this.setStatus('Cargá un MP3 antes de guardar este sonido.','warning');
      return;
    }
    this.stopLoopPreview();
    this.saveButton.disabled=true;
    try{
      const result=await this.callbacks.onSave?.({
        id,
        mode:this.mode,
        design:cloneDesign(this.design),
        assetFile:this.assetFile,
        assetStats:this.assetStats,
      });
      this.setStatus(result?.message||'Sonido de motor guardado.','success');
    }catch(error){
      console.error(error);
      this.setStatus(error.message||'No se pudo guardar.','error');
    }finally{
      this.saveButton.disabled=this.mode==='mp3'&&!this.assetFile;
    }
  }
}
